import { useEffect, useRef, useState } from 'react';
import { waitlistTimerLog } from 'utils/waitlistTimerDebug';

type WaitlistStartTime = string | number | Date | null | undefined;
type WaitlistCountdownDebugMeta = Record<string, unknown>;

type CountdownInput = {
  waitForAcceptStartTime: WaitlistStartTime;
  waitlistTimeOut: number;
  bumpCount?: number;
  now?: () => number;
  intervalMs?: number;
  debugMeta?: WaitlistCountdownDebugMeta;
  debugEnabled?: boolean;
};

type CountdownState = {
  secondsRemaining: number;
  expired: boolean;
  active: boolean;
};

type CountdownSnapshot = CountdownState & {
  remainingMs: number;
};

const SQL_UTC_DATE_TIME = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(?:\.\d+)?$/;
const HAS_TIMEZONE = /(z|[+-]\d{2}:?\d{2})$/i;

const parseWaitlistStartMs = (raw: WaitlistStartTime): number => {
  if (raw instanceof Date) return raw.getTime();
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return new Date(raw < 1_000_000_000_000 ? raw * 1000 : raw).getTime();
  }
  if (typeof raw !== 'string') return Number.NaN;

  const value = raw.trim();
  if (!value) return Number.NaN;

  // MySQL UTC_TIMESTAMP() commonly serializes without a timezone suffix.
  // Unity reads it as UTC; browsers otherwise parse it as local time.
  if (SQL_UTC_DATE_TIME.test(value)) {
    return Date.parse(`${value.replace(' ', 'T')}Z`);
  }

  if (!HAS_TIMEZONE.test(value) && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    return Date.parse(`${value}Z`);
  }

  return Date.parse(value);
};

const computeCountdown = (
  startIso: WaitlistStartTime,
  timeoutSeconds: number,
  now: () => number
): CountdownSnapshot => {
  if (
    startIso === null ||
    startIso === undefined ||
    !Number.isFinite(timeoutSeconds) ||
    timeoutSeconds <= 0
  ) {
    return { secondsRemaining: 0, expired: false, active: false, remainingMs: 0 };
  }
  const startMs = parseWaitlistStartMs(startIso);
  if (Number.isNaN(startMs)) {
    return { secondsRemaining: 0, expired: false, active: false, remainingMs: 0 };
  }
  const deadlineMs = startMs + timeoutSeconds * 1000;
  const remainingMs = Math.max(0, Math.min(timeoutSeconds * 1000, deadlineMs - now()));
  const secondsRemaining = Math.ceil(remainingMs / 1000);
  return {
    secondsRemaining,
    expired: remainingMs === 0,
    active: true,
    remainingMs
  };
};

const computeSeconds = (
  startIso: WaitlistStartTime,
  timeoutSeconds: number,
  now: () => number
): CountdownState => {
  const { remainingMs: _remainingMs, ...state } = computeCountdown(startIso, timeoutSeconds, now);
  return state;
};

const valueType = (value: unknown): string => {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';
  return typeof value;
};

const getCountdownInactiveReason = (
  startIso: WaitlistStartTime,
  timeoutSeconds: number,
  parsedStartMs: number,
  snapshot: CountdownSnapshot
): string | null => {
  if (startIso === null || startIso === undefined) return 'missing-start-time';
  if (!Number.isFinite(timeoutSeconds) || timeoutSeconds <= 0) return 'invalid-timeout';
  if (Number.isNaN(parsedStartMs)) return 'invalid-start-time';
  if (snapshot.expired) return 'already-expired';
  return null;
};

const buildCountdownDecisionDetails = (
  startIso: WaitlistStartTime,
  timeoutSeconds: number,
  nowMs: number,
  snapshot: CountdownSnapshot
) => {
  const parsedStartMs = parseWaitlistStartMs(startIso);
  const hasParsedStart = !Number.isNaN(parsedStartMs);
  const deadlineMs = hasParsedStart ? parsedStartMs + timeoutSeconds * 1000 : Number.NaN;
  const unityElapsedSeconds = hasParsedStart ? (nowMs - parsedStartMs) / 1000 : null;
  const unitySecondsRemaining =
    hasParsedStart && timeoutSeconds > 0 && unityElapsedSeconds !== null
      ? Math.max(0, Math.min(timeoutSeconds, timeoutSeconds - unityElapsedSeconds))
      : null;

  return {
    nowMs,
    nowIso: new Date(nowMs).toISOString(),
    rawStart: startIso,
    rawStartType: valueType(startIso),
    parsedStartMs: hasParsedStart ? parsedStartMs : null,
    parsedStartIso: hasParsedStart ? new Date(parsedStartMs).toISOString() : null,
    timeoutSeconds,
    timeoutSecondsType: valueType(timeoutSeconds),
    deadlineMs: hasParsedStart ? deadlineMs : null,
    deadlineIso: hasParsedStart ? new Date(deadlineMs).toISOString() : null,
    unityElapsedSeconds,
    unitySecondsRemaining,
    remainingMs: snapshot.remainingMs,
    secondsRemaining: snapshot.secondsRemaining,
    active: snapshot.active,
    expired: snapshot.expired,
    inactiveReason: getCountdownInactiveReason(startIso, timeoutSeconds, parsedStartMs, snapshot)
  };
};

const useWaitlistCountdown = ({
  waitForAcceptStartTime,
  waitlistTimeOut,
  bumpCount,
  now = Date.now,
  intervalMs = 250,
  debugMeta,
  debugEnabled = false
}: CountdownInput): CountdownState => {
  // Keep `now` in a ref so callers passing a fresh arrow each render
  // (e.g. tests) do not retrigger the effect / interval.
  const nowRef = useRef(now);
  nowRef.current = now;
  const debugMetaRef = useRef<WaitlistCountdownDebugMeta | undefined>(debugMeta);
  debugMetaRef.current = debugMeta;
  const debugEnabledRef = useRef(debugEnabled);
  debugEnabledRef.current = debugEnabled;
  const log = (stage: string, details: WaitlistCountdownDebugMeta = {}) => {
    if (debugEnabledRef.current) waitlistTimerLog(stage, details);
  };

  const [state, setState] = useState<CountdownState>(() =>
    computeSeconds(waitForAcceptStartTime, waitlistTimeOut, nowRef.current)
  );

  useEffect(() => {
    const initialNowMs = nowRef.current();
    const parsedStartMs = parseWaitlistStartMs(waitForAcceptStartTime);
    const initial = computeCountdown(waitForAcceptStartTime, waitlistTimeOut, () => initialNowMs);
    const logDetails = {
      ...debugMetaRef.current,
      ...buildCountdownDecisionDetails(
        waitForAcceptStartTime,
        waitlistTimeOut,
        initialNowMs,
        initial
      ),
      waitForAcceptStartTime,
      waitlistTimeOut,
      bumpCount,
      initialSecondsRemaining: initial.secondsRemaining,
      initialRemainingMs: initial.remainingMs,
      initialExpired: initial.expired,
      initialActive: initial.active,
      intervalMs
    };
    log('countdown:decision', logDetails);
    log('countdown:init', logDetails);
    setState(initial);
    if (
      waitForAcceptStartTime === null ||
      waitForAcceptStartTime === undefined ||
      waitlistTimeOut <= 0 ||
      !initial.active
    ) {
      log('countdown:inactive', {
        ...logDetails,
        reason:
          waitForAcceptStartTime === null || waitForAcceptStartTime === undefined
            ? 'missing-start-time'
            : waitlistTimeOut <= 0
            ? 'invalid-timeout'
            : 'invalid-start-time'
      });
      return;
    }

    if (initial.expired) {
      log('countdown:already-expired', logDetails);
      return;
    }

    log('countdown:start-date-diff-ticker', {
      ...logDetails,
      deadlineIso: new Date(parsedStartMs + waitlistTimeOut * 1000).toISOString()
    });
    let lastHeartbeatSecond = Math.floor(initialNowMs / 1000);
    const interval = setInterval(() => {
      setState((prev) => {
        const nowMs = nowRef.current();
        const next = computeCountdown(waitForAcceptStartTime, waitlistTimeOut, () => nowMs);
        const heartbeatSecond = Math.floor(nowMs / 1000);
        if (heartbeatSecond !== lastHeartbeatSecond) {
          lastHeartbeatSecond = heartbeatSecond;
          log('countdown:interval-fired', {
            ...debugMetaRef.current,
            ...buildCountdownDecisionDetails(waitForAcceptStartTime, waitlistTimeOut, nowMs, next),
            waitForAcceptStartTime,
            waitlistTimeOut,
            bumpCount,
            previousSecondsRemaining: prev.secondsRemaining,
            previousActive: prev.active,
            previousExpired: prev.expired
          });
        }
        if (
          prev.secondsRemaining === next.secondsRemaining &&
          prev.expired === next.expired &&
          prev.active === next.active
        ) {
          return prev;
        }
        log('countdown:tick', {
          ...debugMetaRef.current,
          waitForAcceptStartTime,
          waitlistTimeOut,
          bumpCount,
          nowMs,
          secondsRemaining: next.secondsRemaining,
          remainingMs: next.remainingMs,
          expired: next.expired
        });
        if (next.expired) {
          log('countdown:expired', {
            ...debugMetaRef.current,
            waitForAcceptStartTime,
            waitlistTimeOut,
            bumpCount,
            nowMs
          });
          clearInterval(interval);
        }
        return next;
      });
    }, intervalMs);
    return () => {
      log('countdown:cleanup', {
        ...debugMetaRef.current,
        waitForAcceptStartTime,
        waitlistTimeOut,
        bumpCount
      });
      clearInterval(interval);
    };
  }, [waitForAcceptStartTime, waitlistTimeOut, bumpCount, intervalMs]);

  return state;
};

export default useWaitlistCountdown;
export { computeSeconds, parseWaitlistStartMs };
